// =====================================================
// Entry — bootstraps the React tree and applies persisted theme
// before first paint so the initial frame doesn't flash light/dark.
//
// Phase-14.5 C3: dropped @ts-nocheck.
// =====================================================
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { bootstrapLocale, renderLocaleBootFailure } from "./i18n/bootstrap";
import { initI18n } from "./i18n";
import { firstPaintNamespaces } from "./i18n/route-namespaces";

// Phase-A R7.2.a/b — self-host fonts via @fontsource. Replaces the
// Google Fonts <link> in index.html (which transferred ~199 KiB
// from a third-party origin and was a §1 Lighthouse Perf hit).
//
// Q1 Option B1 per owner ack 2026-05-24: keep all 3 design fonts
// (Vazirmatn + Bricolage Grotesque + JetBrains Mono) but trim
// to the weights actually used in styles.css.
//
// Audit (from `grep font-weight` on styles.css + design intent):
//   - Vazirmatn (--f-sans): primary Persian font. Used at 500/600/700/800.
//     Drop 300/400/900 (negligible CSS hits or covered by browser defaults).
//   - Bricolage Grotesque (--f-display): heading display font. Used at
//     500/600/700 for the .h-1/.h-2/.h-display rules. Drop 400/800.
//   - JetBrains Mono (--f-mono): eyebrow + numeric strings. Used at
//     400/500/600. Drop 700.
//
// Each import pulls the woff2 + a tiny @font-face CSS that registers
// the family name. The font-face uses font-display: swap by default
// (same behavior as the Google Fonts version). Bundle impact: smaller
// than the 199 KiB Google Fonts transfer because we ship trimmed weights
// instead of all-7-Vazirmatn + variable-Bricolage + all-4-JetBrains.
import "@fontsource/vazirmatn/500.css";
import "@fontsource/vazirmatn/600.css";
import "@fontsource/vazirmatn/700.css";
import "@fontsource/vazirmatn/800.css";
import "@fontsource/bricolage-grotesque/500.css";
import "@fontsource/bricolage-grotesque/600.css";
import "@fontsource/bricolage-grotesque/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/600.css";

// ============================================================
// R-Landing-v2 Commit A — SW + caches dispose for demo window.
// Per D45 (SW disabled temporarily) + D47 (R-Landing-v2 approved).
// Any existing SW from prior deploys gets unregistered, all caches
// deleted. Future deploys won't register a new SW because VitePWA
// is disabled (vite.config.js). Fire-and-forget: does not block
// first paint or any other bootstrap.
// Paired with sw-recovery.js KILL_FLAG bump (belt-and-suspenders).
// Re-enable plan: revert this block + ship R7.0 (network-first
// cache strategy) per D42.
// ============================================================
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => {});
  if (typeof caches !== "undefined" && typeof caches.keys === "function") {
    caches.keys()
      .then((keys) => keys.forEach((k) => caches.delete(k)))
      .catch(() => {});
  }
}

import "../styles.css";
// Phase-A R6: classroom redesign CSS, scoped under .r6-classroom-shell so
// it never bleeds into other pages.
import "../styles-r6-classroom.css";

// Theme: dark mode is DISABLED for now (owner request, D-2026-06-13). The site
// is forced to the light (white + navy) palette regardless of any persisted
// "digiu_theme" value, and the toggle button is removed from the top bar. The
// dark tokens stay in styles for a future re-enable — flip this back to read
// localStorage when dark returns.
document.documentElement.setAttribute("data-theme", "light");

const rootEl = document.getElementById("root");
if (!rootEl) {
  // index.html ships with <div id="root"></div>. If this throws, the
  // template was edited or something hijacked the element id — both
  // are fatal and worth a clear error in the console.
  throw new Error("[main] #root element not found in index.html");
}

// S-I18N-W1a — resolve the locale and load its `common` + `shell` catalogues
// BEFORE the first render. Synchronous resolution (cookie → navigator) writes
// `<html lang dir>` immediately; the await is only the two JSON chunks, and
// rendering earlier would paint Persian and then swap script mid-frame.
// `.then` rather than top-level await: the build target is es2020.
const bootLocale = bootstrapLocale();
// کاتالوگِ خودِ مسیر هم همین‌جا و *موازی* با شل خواسته می‌شود: تنبل‌بودنش
// روی Slow-3G یعنی یک رفت‌وبرگشت که کاربر در آن کلیدِ خام می‌خواند
// (`i18n/route-namespaces.ts` عددهای اندازه‌گیری‌شده را دارد).
void initI18n(bootLocale, firstPaintNamespaces(window.location.pathname)).then(() => {
  ReactDOM.createRoot(rootEl).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}, () => renderLocaleBootFailure(rootEl, bootLocale));
